/**
 * Static, curated guide for each Popoyo (Tola, Rivas) surf spot. Merged at
 * render-time with live conditions from /api/surf/{beach_id}. Slugs match the
 * backend BEACHES table so `/surf/:slug` resolves to `/api/surf/:slug`.
 *
 * Each beach carries an `i18n.es` block with Spanish translations for every
 * localized field. Use `localizeBeach(beach, lang)` below to pick the correct
 * copy at render time (English is the fallback).
 */
export const BEACH_GUIDES = {
  "playa-popoyo": {
    id: "playa-popoyo",
    slug: "popoyo",
    name: "Playa Popoyo",
    photoUrl: "https://images.unsplash.com/photo-1513569143478-b38b2c0ef97f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzdXJmJTIwd2F2ZSUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzkwMTMxMzU2fDA&ixlib=rb-4.1.0&q=85",
    tagline: "The crown jewel — a world-class A-frame reef that peels both left and right.",
    difficulty: "Intermediate to Advanced",
    difficultyLevel: 4,
    waveStyle: "A-frame reef break — hollow lefts and rights",
    bestTide: "Mid tide (works on all tides)",
    bestWind: "Offshore — Lake Nicaragua winds blow it clean nearly year-round",
    boardRecommended: "Shortboard 6'0\"–6'4\" · step-up 6'6\"+ on bigger days",
    bestSeasons: [
      { months: "Apr – Oct", label: "Prime south swell (wet season)", quality: 5 },
      { months: "Nov – Mar", label: "Cleaner but smaller (dry season)", quality: 3 },
    ],
    hazards: ["Shallow rock & reef bottom — booties recommended", "Strong current on the inside when it's big", "Gets crowded at peak season"],
    directions: {
      from: "Managua (MGA airport)",
      distance: "~2.5 hr drive · last stretch unpaved",
      routeNote: "4×4 recommended for the dirt access road — cash-only area, no ATMs",
      driveHref: "https://www.google.com/maps/dir/Managua/Playa+Popoyo,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Popoyo,+Nicaragua",
    },
    localTips: [
      "The offshore wind is created by Lake Nicaragua, so it stays clean even in the afternoon",
      "Bring cash — there are no ATMs in Popoyo or Guasacate",
      "Booties save your feet on the reef at low tide",
      "Paddle out through the channel to avoid the shallow inside reef",
    ],
    color: "#2EC4B6",
    i18n: {
      es: {
        tagline: "La joya de la corona — un reef A-frame de clase mundial que rompe a izquierda y derecha.",
        difficulty: "Intermedio a Avanzado",
        waveStyle: "Reef break A-frame — izquierdas y derechas huecas",
        bestTide: "Marea media (funciona en todas las mareas)",
        bestWind: "Offshore — los vientos del Lago de Nicaragua la mantienen limpia casi todo el año",
        boardRecommended: "Tabla corta 6'0\"–6'4\" · step-up 6'6\"+ en días grandes",
        bestSeasons: [
          { months: "Abr – Oct", label: "Swell principal del sur (temporada lluviosa)", quality: 5 },
          { months: "Nov – Mar", label: "Más limpio pero más pequeño (temporada seca)", quality: 3 },
        ],
        hazards: ["Fondo de roca y arrecife poco profundo — se recomiendan botines", "Corriente fuerte en el interior cuando está grande", "Se llena en temporada alta"],
        directions: {
          from: "Managua (aeropuerto MGA)",
          distance: "~2.5 hr en carro · último tramo sin pavimentar",
          routeNote: "Se recomienda 4×4 para el camino de tierra — zona de solo efectivo, sin cajeros",
        },
        localTips: [
          "El viento offshore lo genera el Lago de Nicaragua, así que se mantiene limpio incluso por la tarde",
          "Trae efectivo — no hay cajeros en Popoyo ni Guasacate",
          "Los botines te salvan los pies en el arrecife con marea baja",
          "Rema por el canal para evitar el arrecife poco profundo del interior",
        ],
      },
    },
  },

  "playa-colorado": {
    id: "playa-colorado",
    slug: "colorado",
    name: "Playa Colorado",
    photoUrl: "https://images.unsplash.com/photo-1616645728806-838c6bf184af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxiYXJyZWwlMjB3YXZlfGVufDB8fHx8MTc5MDEzMTM1Nnww&ixlib=rb-4.1.0&q=85",
    tagline: "World-class barreling beach break inside Hacienda Iguana.",
    difficulty: "Advanced",
    difficultyLevel: 5,
    waveStyle: "Fast, hollow river-mouth beach break",
    bestTide: "Mid to high",
    bestWind: "Offshore from the east / northeast",
    boardRecommended: "Shortboard 5'10\"–6'4\" · step-up for size",
    bestSeasons: [
      { months: "Apr – Oct", label: "Prime barreling swell (wet season)", quality: 5 },
      { months: "Nov – Mar", label: "Smaller but fun (dry season)", quality: 3 },
    ],
    hazards: ["Powerful close-out sections", "Heavy shore break at size", "Public beach inside a gated community — respect the signage"],
    directions: {
      from: "Popoyo / Guasacate",
      distance: "~20 min · inside Hacienda Iguana",
      routeNote: "Enter through the Hacienda Iguana gate — the beach itself is public",
      driveHref: "https://www.google.com/maps/dir/Playa+Popoyo/Playa+Colorado,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Colorado,+Nicaragua",
    },
    localTips: [
      "Best on a solid SW swell — it turns into a barreling machine",
      "Get there early before the crowd builds",
      "Panga Drops is right next door if Colorado is closing out",
      "Watch the river mouth after heavy rain",
    ],
    color: "#FF6B6B",
    i18n: {
      es: {
        tagline: "Beach break tubero de clase mundial dentro de Hacienda Iguana.",
        difficulty: "Avanzado",
        waveStyle: "Beach break rápido y hueco en desembocadura de río",
        bestTide: "Media a alta",
        bestWind: "Offshore del este / noreste",
        boardRecommended: "Tabla corta 5'10\"–6'4\" · step-up para tamaño",
        bestSeasons: [
          { months: "Abr – Oct", label: "Swell tubero principal (temporada lluviosa)", quality: 5 },
          { months: "Nov – Mar", label: "Más pequeño pero divertido (temporada seca)", quality: 3 },
        ],
        hazards: ["Secciones cerradas potentes", "Shore break pesado con tamaño", "Playa pública dentro de comunidad privada — respeta la señalización"],
        directions: {
          from: "Popoyo / Guasacate",
          distance: "~20 min · dentro de Hacienda Iguana",
          routeNote: "Entra por el portón de Hacienda Iguana — la playa es pública",
        },
        localTips: [
          "Mejor con un swell del SO sólido — se convierte en una máquina de tubos",
          "Llega temprano antes de que se llene",
          "Panga Drops está justo al lado si Colorado está cerrando",
          "Cuidado con la desembocadura del río después de lluvias fuertes",
        ],
      },
    },
  },

  "playa-santana": {
    id: "playa-santana",
    slug: "santana",
    name: "Playa Santana",
    photoUrl: "https://images.unsplash.com/photo-1526342122811-2a9c8512023d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
    tagline: "Consistent beach-break peaks plus the reeling Lance's Left point.",
    difficulty: "Intermediate",
    difficultyLevel: 3,
    waveStyle: "Beach break with a left point (Lance's Left)",
    bestTide: "Mid tide",
    bestWind: "Offshore from the east",
    boardRecommended: "Shortboard to fish 5'10\"–6'6\"",
    bestSeasons: [
      { months: "Apr – Oct", label: "Consistent peaks (wet season)", quality: 5 },
      { months: "Nov – Mar", label: "Smaller, cleaner (dry season)", quality: 3 },
    ],
    hazards: ["Closes out on big swells", "Rocks near the point at low tide"],
    directions: {
      from: "Popoyo",
      distance: "~15 min · Hacienda Iguana area",
      routeNote: "Dirt roads — 4×4 handy in the wet season",
      driveHref: "https://www.google.com/maps/dir/Playa+Popoyo/Playa+Santana,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Santana,+Nicaragua",
    },
    localTips: [
      "Multiple peaks — spread out to find your own wave",
      "Lance's Left point wakes up on a bigger swell",
      "A great intermediate step-up from mellow Guasacate",
      "Less crowded than Colorado on most days",
    ],
    color: "#FFB84D",
    i18n: {
      es: {
        tagline: "Picos de beach break consistentes más la izquierda larga de Lance's Left.",
        difficulty: "Intermedio",
        waveStyle: "Beach break con una punta de izquierda (Lance's Left)",
        bestTide: "Marea media",
        bestWind: "Offshore del este",
        boardRecommended: "De tabla corta a fish 5'10\"–6'6\"",
        bestSeasons: [
          { months: "Abr – Oct", label: "Picos consistentes (temporada lluviosa)", quality: 5 },
          { months: "Nov – Mar", label: "Más pequeño y limpio (temporada seca)", quality: 3 },
        ],
        hazards: ["Cierra con swells grandes", "Rocas cerca de la punta con marea baja"],
        directions: {
          from: "Popoyo",
          distance: "~15 min · zona de Hacienda Iguana",
          routeNote: "Caminos de tierra — 4×4 útil en temporada lluviosa",
        },
        localTips: [
          "Varios picos — sepárate para encontrar tu propia ola",
          "La punta de Lance's Left despierta con swell más grande",
          "Buen paso intermedio desde el tranquilo Guasacate",
          "Menos gente que Colorado la mayoría de los días",
        ],
      },
    },
  },

  "playa-guasacate": {
    id: "playa-guasacate",
    slug: "guasacate",
    name: "Playa Guasacate",
    photoUrl: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMGJlYWNoJTIwc3Vuc2V0fGVufDB8fHx8MTc5MDEzMTM1Nnww&ixlib=rb-4.1.0&q=85",
    tagline: "The long, forgiving beach break north of Popoyo — peaks for every level.",
    difficulty: "All levels",
    difficultyLevel: 2,
    waveStyle: "Open beach break, sandy bottom",
    bestTide: "Mid tide",
    bestWind: "Offshore from the east",
    boardRecommended: "Anything — foamie & longboard for learners, shortboard when it's on",
    bestSeasons: [
      { months: "Nov – Mar", label: "Glassy & mellow — perfect for learning", quality: 5 },
      { months: "Apr – Oct", label: "Bigger, more power", quality: 4 },
    ],
    hazards: ["Occasional rip current between sandbars", "Fewer hazards than the reefs — a great learning zone"],
    directions: {
      from: "Playa Popoyo main reef",
      distance: "~5 min north along the Guasacate peninsula",
      routeNote: "Paved-ish road along the peninsula — easy access",
      driveHref: "https://www.google.com/maps/dir/Playa+Popoyo/Playa+Guasacate,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Guasacate,+Nicaragua",
    },
    localTips: [
      "Where most surf schools bring beginners",
      "Sandy bottom means softer wipeouts",
      "It's a long stretch — walk to find an empty peak",
      "Sunset here is absolutely unreal",
    ],
    color: "#4EA8DE",
    i18n: {
      es: {
        tagline: "El beach break largo y perdonador al norte de Popoyo — picos para todos los niveles.",
        difficulty: "Todos los niveles",
        waveStyle: "Beach break abierto, fondo de arena",
        bestTide: "Marea media",
        bestWind: "Offshore del este",
        boardRecommended: "Cualquier tabla — foam y longboard para principiantes, tabla corta cuando está grande",
        bestSeasons: [
          { months: "Nov – Mar", label: "Glassy y tranquilo — perfecto para aprender", quality: 5 },
          { months: "Abr – Oct", label: "Más grande, más potencia", quality: 4 },
        ],
        hazards: ["Corriente de resaca ocasional entre bancos de arena", "Menos peligros que los reefs — excelente zona para aprender"],
        directions: {
          from: "Reef principal de Playa Popoyo",
          distance: "~5 min al norte por la península de Guasacate",
          routeNote: "Camino decente por la península — acceso fácil",
        },
        localTips: [
          "Donde la mayoría de las escuelas de surf traen a los principiantes",
          "El fondo de arena hace las caídas más suaves",
          "Es una playa larga — camina para encontrar un pico vacío",
          "El atardecer aquí es increíble",
        ],
      },
    },
  },

  "panga-drops": {
    id: "panga-drops",
    slug: "panga-drops",
    name: "Panga Drops",
    photoUrl: "https://images.unsplash.com/photo-1559627755-42212e5c5fdf?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
    tagline: "A deep-water reef that holds size when everywhere else closes out.",
    difficulty: "Advanced",
    difficultyLevel: 4,
    waveStyle: "Deep-water reef break — mostly rights",
    bestTide: "All tides, mid best",
    bestWind: "Offshore from the east",
    boardRecommended: "Step-up 6'4\"+ · shortboard on smaller days",
    bestSeasons: [
      { months: "Apr – Oct", label: "Prime swell — holds the size", quality: 5 },
      { months: "Nov – Mar", label: "Smaller & cleaner", quality: 3 },
    ],
    hazards: ["Long deep-water paddle — bring fitness", "Panga boat traffic — stay aware", "Reef bottom"],
    directions: {
      from: "Colorado / Hacienda Iguana",
      distance: "~5 min · inside Hacienda Iguana",
      routeNote: "Long paddle from Colorado, or grab a panga lift out",
      driveHref: "https://www.google.com/maps/dir/Playa+Colorado/Panga+Drops,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Hacienda+Iguana,+Nicaragua",
    },
    localTips: [
      "Super consistent — often surfable when Colorado is closing out",
      "The rights are the money wave here",
      "Long paddle — many surfers grab a panga out from the beach",
      "Deeper water means it holds bigger swells cleanly",
    ],
    color: "#1B6B93",
    i18n: {
      es: {
        tagline: "Un reef de aguas profundas que aguanta el tamaño cuando todo lo demás cierra.",
        difficulty: "Avanzado",
        waveStyle: "Reef break de aguas profundas — mayormente derechas",
        bestTide: "Todas las mareas, media es la mejor",
        bestWind: "Offshore del este",
        boardRecommended: "Step-up 6'4\"+ · tabla corta en días pequeños",
        bestSeasons: [
          { months: "Abr – Oct", label: "Swell principal — aguanta el tamaño", quality: 5 },
          { months: "Nov – Mar", label: "Más pequeño y limpio", quality: 3 },
        ],
        hazards: ["Remada larga en aguas profundas — necesitas fondo físico", "Tráfico de pangas — mantente atento", "Fondo de arrecife"],
        directions: {
          from: "Colorado / Hacienda Iguana",
          distance: "~5 min · dentro de Hacienda Iguana",
          routeNote: "Remada larga desde Colorado, o toma una panga hacia afuera",
        },
        localTips: [
          "Súper consistente — muchas veces surfeable cuando Colorado está cerrando",
          "Las derechas son la ola estrella aquí",
          "Remada larga — muchos surfistas toman una panga desde la playa",
          "El agua más profunda hace que aguante swells grandes limpiamente",
        ],
      },
    },
  },

  "playa-gigante": {
    id: "playa-gigante",
    slug: "gigante",
    name: "Playa Gigante",
    photoUrl: "https://images.unsplash.com/photo-1672753519498-a8c0c9e57551?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
    tagline: "A mellow fishing-village bay — surf charters, sport fishing, easy vibes.",
    difficulty: "Beginner to Intermediate",
    difficultyLevel: 2,
    waveStyle: "Sheltered bay beach break + boat access to outer reefs",
    bestTide: "Mid tide",
    bestWind: "Offshore east / calm",
    boardRecommended: "Longboard & fun-shape in the bay; shortboard on charter trips",
    bestSeasons: [
      { months: "Nov – Mar", label: "Mellow & friendly in the bay", quality: 4 },
      { months: "Apr – Oct", label: "Outer reefs fire (boat trips)", quality: 5 },
    ],
    hazards: ["Fishing boats in the bay — surf the marked areas", "Outer reefs are advanced-only"],
    directions: {
      from: "Popoyo",
      distance: "~40 min south · mostly paved",
      routeNote: "Easiest access of the region — good base for boat trips",
      driveHref: "https://www.google.com/maps/dir/Playa+Popoyo/Playa+Gigante,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Gigante,+Nicaragua",
    },
    localTips: [
      "Base yourself here for boat trips to Colorado & Panga Drops",
      "Amazing seafood straight off the fishing pangas",
      "The bay is a family-friendly swim spot too",
      "Sport-fishing charters leave at dawn — book the night before",
    ],
    color: "#2EC4B6",
    i18n: {
      es: {
        tagline: "Una bahía de pueblo pesquero tranquila — charters de surf, pesca deportiva y buen ambiente.",
        difficulty: "Principiante a Intermedio",
        waveStyle: "Beach break de bahía protegida + acceso en bote a los reefs exteriores",
        bestTide: "Marea media",
        bestWind: "Offshore del este / calmo",
        boardRecommended: "Longboard y fun-shape en la bahía; tabla corta en los charters",
        bestSeasons: [
          { months: "Nov – Mar", label: "Tranquilo y amigable en la bahía", quality: 4 },
          { months: "Abr – Oct", label: "Los reefs exteriores explotan (viajes en bote)", quality: 5 },
        ],
        hazards: ["Botes de pesca en la bahía — surfea las zonas marcadas", "Los reefs exteriores son solo para avanzados"],
        directions: {
          from: "Popoyo",
          distance: "~40 min al sur · mayormente pavimentado",
          routeNote: "El acceso más fácil de la región — buena base para viajes en bote",
        },
        localTips: [
          "Hospédate aquí para viajes en bote a Colorado y Panga Drops",
          "Mariscos increíbles recién bajados de las pangas",
          "La bahía también es un lugar familiar para nadar",
          "Los charters de pesca deportiva salen al amanecer — reserva la noche anterior",
        ],
      },
    },
  },
};

export const BEACH_SLUGS = Object.values(BEACH_GUIDES).map((b) => b.slug);

/**
 * Return a beach guide with its translatable fields replaced by the caller's
 * language. Falls back to English (the source) when a key is missing so newly
 * added beaches never render blank.
 */
export function localizeBeach(beach, lang) {
  if (!beach) return beach;
  const tr = beach.i18n?.[lang];
  if (!tr) return beach;
  return {
    ...beach,
    tagline: tr.tagline || beach.tagline,
    difficulty: tr.difficulty || beach.difficulty,
    waveStyle: tr.waveStyle || beach.waveStyle,
    bestTide: tr.bestTide || beach.bestTide,
    bestWind: tr.bestWind || beach.bestWind,
    boardRecommended: tr.boardRecommended || beach.boardRecommended,
    bestSeasons: Array.isArray(tr.bestSeasons) && tr.bestSeasons.length
      ? tr.bestSeasons.map((s, i) => ({ ...beach.bestSeasons[i], ...s }))
      : beach.bestSeasons,
    hazards: Array.isArray(tr.hazards) && tr.hazards.length ? tr.hazards : beach.hazards,
    localTips: Array.isArray(tr.localTips) && tr.localTips.length ? tr.localTips : beach.localTips,
    directions: {
      ...beach.directions,
      ...(tr.directions || {}),
    },
  };
}
